import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Tipos
type Content = {
  [key: string]: {
    [key: string]: string;
  };
};

type ContentAction = 
  | { type: 'UPDATE_SECTION'; sectionId: string; content: { [key: string]: string } }
  | { type: 'LOAD_SAVED_CONTENT'; content: Content };

type ContentContextType = {
  content: Content;
  updateSection: (sectionId: string, newContent: { [key: string]: string }) => void;
};

// Conteúdo padrão
const defaultContent: Content = {
  hero: {
    quote: 'A terapia é um espaço de autoconhecimento, crescimento e transformação.',
    description: 'Especialista em Terapia Cognitivo-Comportamental, oferecendo um espaço seguro e acolhedor para seu processo terapêutico.'
  },
  about: {
    title: 'Sobre Mim',
    description: 'Sou psicóloga clínica com formação em Terapia Cognitivo-Comportamental. Minha abordagem é centrada no cliente, buscando proporcionar um ambiente acolhedor e seguro para o processo terapêutico.',
    formacao: 'Psicóloga formada com especialização em Terapia Cognitivo-Comportamental',
    experiencia: 'Anos de prática clínica com atendimento humanizado e acolhedor',
    abordagem: 'Foco em resultados com técnicas baseadas em evidências científicas'
  },
  services: {
    title: 'Serviços',
    description: 'Atendimento psicológico personalizado e humanizado',
    service1Title: 'Psicoterapia Individual',
    service1Description: 'Atendimento personalizado para adultos, focado no autoconhecimento e desenvolvimento pessoal.',
    service2Title: 'Terapia Online',
    service2Description: 'Sessões realizadas por videoconferência, oferecendo flexibilidade e comodidade.',
    service3Title: 'Palestras',
    service3Description: 'Apresentações e workshops sobre temas relacionados à saúde mental, desenvolvimento pessoal e bem-estar emocional para empresas e grupos.'
  },
  contact: {
    title: 'Como Chegar',
    description: 'Entre em contato e agende sua consulta',
    address: 'Rua S-2, 913 - St. Bela Vista, Goiânia - GO',
    phone: '(62) 98261-5656',
    instagram: '@psi.ke',
    schedule: 'Segunda a Sexta, 08:00 - 20:00'
  }
};

// Reducer para gerenciar as atualizações de estado
function contentReducer(state: Content, action: ContentAction): Content {
  switch (action.type) {
    case 'UPDATE_SECTION':
      const newState = {
        ...state,
        [action.sectionId]: {
          ...state[action.sectionId],
          ...action.content
        }
      };
      // Salvar no localStorage sempre que houver uma atualização
      localStorage.setItem('websiteContent', JSON.stringify(newState));
      return newState;

    case 'LOAD_SAVED_CONTENT':
      return action.content;

    default:
      return state;
  }
}

// Criar o contexto
const ContentContext = createContext<ContentContextType | undefined>(undefined);

// Hook personalizado para usar o contexto
export function useContent() {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
}

// Provider Component
export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [content, dispatch] = useReducer(contentReducer, defaultContent);

  // Carregar conteúdo salvo quando o componente montar
  useEffect(() => {
    try {
      const savedContent = localStorage.getItem('websiteContent');
      if (savedContent) {
        const parsedContent = JSON.parse(savedContent);
        dispatch({ type: 'LOAD_SAVED_CONTENT', content: parsedContent });
      }
    } catch (error) {
      console.error('Error loading saved content:', error);
    }
  }, []);

  const updateSection = (sectionId: string, newContent: { [key: string]: string }) => {
    dispatch({ type: 'UPDATE_SECTION', sectionId, content: newContent });
  };

  return (
    <ContentContext.Provider value={{ content, updateSection }}>
      {children}
    </ContentContext.Provider>
  );
};
